import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Card, Form } from 'react-bootstrap';
import { Switch } from '@headlessui/react';

import { classNames } from '../helpers';
import { get, patch } from '../api2';

import Page from '../Page';
import SecurityTlds from './SecurityTlds';

const previewSettings = new Set([
  'newlyActiveDomains',
  'freeHostingDomains',
  'tunnelingEndpoints',
  'dataDropServices',
  'residentialHosting',
  'untrustedCertificates',
  'fastFluxNetworks',
  'dnsDataExfiltration',
  'dnsPayloadDelivery',
  'decentralizedWebGateways',
  'highRiskTlds',
]);

const restrictedSettings = new Set(['fastFluxNetworks', 'dnsDataExfiltration']);

function Setting({ setting, translation, checked, onChange }) {
  const { t } = useTranslation();
  const name = t(`security.${translation}.name`);
  const description = t(`security.${translation}.description`);
  const action = t(`security.${translation}.enable`);
  const preview = previewSettings.has(setting);
  const disclaimer = setting === 'ddns' && t('security.ddns.disclaimer');

  if (process.env.REACT_APP_TAILWIND) {
    return (
      <div className="tw-overflow-hidden tw-rounded-md tw-border dark:tw-border-slate-800">
        <div className="tw-px-6 tw-py-5">
          <h3 className="tw-text-lg tw-font-medium tw-leading-6 tw-text-gray-900 dark:tw-text-slate-200">
            <span className="tw-align-middle">{name}</span>
            {preview && (
              <span className="tw-ml-2 tw-inline-flex tw-items-center tw-rounded-full tw-bg-blue-100 tw-px-2.5 tw-align-middle tw-text-[11px] tw-font-medium tw-uppercase tw-leading-5 tw-text-blue-800">
                {t('global.preview')}
              </span>
            )}
          </h3>
          <div className="tw-mt-2 tw-text-sm tw-text-gray-500">{description}</div>
          {disclaimer && <div className="tw-mt-2 tw-text-sm tw-text-gray-400">{disclaimer}</div>}
        </div>
        <div className="tw-border-t tw-bg-slate-50 tw-px-6 tw-py-4 dark:tw-border-slate-800 dark:tw-bg-slate-800/30">
          <Switch.Group as="div" className="tw-items-top tw-flex">
            <Switch
              checked={checked}
              disabled={!onChange}
              onChange={onChange}
              className={classNames(
                checked ? 'tw-bg-nextdnsblue' : 'tw-bg-slate-200 dark:tw-bg-slate-600',
                'tw-relative tw-inline-flex tw-h-5 tw-w-9 tw-flex-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-2 tw-border-transparent tw-transition-colors tw-duration-200 tw-ease-in-out',
              )}
            >
              <span
                aria-hidden="true"
                className={classNames(
                  checked ? 'tw-translate-x-4' : 'tw-translate-x-0',
                  'tw-pointer-events-none tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-shadow tw-ring-0 tw-transition tw-duration-200 tw-ease-in-out',
                )}
              />
            </Switch>
            <Switch.Label as="span" className="tw-ml-3 tw-text-sm tw-text-gray-500">
              {action}
            </Switch.Label>
          </Switch.Group>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <Card.Body>
        <div className="d-flex align-items-center">
          <h5>{name}</h5>
          {preview && (
            <div
              style={{
                background: '#dbeafe',
                borderRadius: 3,
                padding: '1px 3px',
                marginTop: -7,
                marginLeft: 8,
                fontSize: 10,
                color: '#1e40af',
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            >
              {t('global.preview')}
            </div>
          )}
        </div>
        <div style={{ opacity: 0.6, fontSize: '0.9em' }}>{description}</div>
        {disclaimer && (
          <div className="mt-2" style={{ opacity: 0.4, fontSize: '0.9em' }}>
            {disclaimer}
          </div>
        )}
      </Card.Body>
      <Card.Footer className="text-muted">
        <div className="d-flex">
          <div className="me-1">
            <Form.Switch
              id={`security-${setting}`}
              label=""
              aria-label={action}
              checked={checked}
              onChange={onChange && ((event) => onChange(event.target.checked))}
              disabled={!onChange}
            />
          </div>
          <div className="flex-grow-1 d-flex align-items-center" style={{ fontSize: '0.9em' }}>
            <div>{action}</div>
          </div>
        </div>
      </Card.Footer>
    </Card>
  );
}

export default function Security({ profile, role }) {
  const { t } = useTranslation();
  const [state, setState] = useState();

  useEffect(() => {
    setState();
    return get(`/profiles/${profile}/security`, ({ data }) => setState(data));
  }, [profile]);

  const toggle = useCallback(
    (setting, enabled) => {
      setState((state) => ({ ...state, [setting]: enabled }));
      patch(`/profiles/${profile}/security`, { [setting]: enabled });
    },
    [profile],
  );

  const settings = [
    ['threatIntelligenceFeeds', 'feeds'],
    ['aiThreatDetection', 'ai'],
    ['googleSafeBrowsing'],
    ['nrd'],
    ['newlyActiveDomains'],
    ['freeHostingDomains'],
    ['ddns'],
    ['tunnelingEndpoints'],
    ['residentialHosting'],
    ['untrustedCertificates'],
    ['dataDropServices'],
    ['dnsDataExfiltration'],
    ['dnsPayloadDelivery'],
    ['fastFluxNetworks'],
    ['dnsRebinding'],
    ['cryptojacking'],
    ['decentralizedWebGateways'],
    ['parking', 'parked'],
    ['highRiskTlds'],
    ['dga'],
    ['tlds'],
    ['csam'],
  ].filter(([setting]) => profile === '9453d3' || !restrictedSettings.has(setting));

  return (
    <Page title={t('pages.security')} profile={profile}>
      {state &&
        settings.map(([setting, translation = setting], index) => {
          const content =
            setting === 'tlds' ? (
              <SecurityTlds profile={profile} role={role} blockedTLDs={state.tlds} />
            ) : (
              <Setting
                setting={setting}
                translation={translation}
                checked={state[setting]}
                onChange={role === 'viewer' ? null : (enabled) => toggle(setting, enabled)}
              />
            );

          return process.env.REACT_APP_TAILWIND ? (
            <div key={setting} className={index > 0 ? 'tw-mt-4' : undefined}>
              {content}
            </div>
          ) : (
            <Row key={setting} className={index < settings.length - 1 ? 'mb-3' : undefined}>
              <Col>{content}</Col>
            </Row>
          );
        })}
    </Page>
  );
}
